import {HttpHeaders, HttpInterceptorFn, HttpResponse} from '@angular/common/http';
import { of } from 'rxjs';
import { throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { delay } from 'rxjs/operators';
import { Absence } from './models/absence.model';

export const mockInterceptor: HttpInterceptorFn = (req, next) => {
    if (!environment.useMocks) {
        return next(req);
    }

    // Simulate network delay
    const delayMs = 500;

    // Mock responses based on endpoints
    switch (req.url) {
        case '/api/login':
            // Mock successful login
            return of(new HttpResponse({ 
                status: 200,
                body: { success: true, message: 'Login successful' }
            })).pipe(delay(delayMs));

        case '/api/is-authenticated':
            // Mock authentication status - always return true for Bolt environment
            if (environment.production) {
                // In production, let the request go through normally
                return next(req);
            } else {
                return of(new HttpResponse({ 
                    status: 200,
                    body: { authenticated: true }
                })).pipe(delay(delayMs));
            }

        case '/api/user':
            // Mock user information
            return of(new HttpResponse({
                body: {
                    username: "bolt-user",
                    consultant_id: "CONS001",
                    client_id: "CLIENT001"
                }
            })).pipe(delay(delayMs));

        case '/api/logout':
            // Mock logout
            return of(new HttpResponse({ 
                status: 200,
                body: { success: true, message: 'Logout successful' }
            })).pipe(delay(delayMs));

        case '/api/datev/is-authenticated':
            if (environment.production) {
                return next(req);
            } else {
                return of(new HttpResponse({ body: true })).pipe(delay(delayMs));
            }

        case '/api/datev/user-info':
            if (environment.production) {
                return next(req);
            } else {
                return of(new HttpResponse({
                    body: {
                        name: "Test User",
                        email: "test@example.com",
                        family_name: "User",
                        email_verified: true,
                        sub: "mock-sub",
                        is_member: true,
                        account_id: "mock-account-id"
                    }
                })).pipe(delay(delayMs));
            }

        case '/api/datev/refresh-tokens':
            return of(new HttpResponse({
                body: {
                    success: true,
                    message: "Login erfolgreich verlängert"
                }
            })).pipe(delay(delayMs));

        case '/api/datev/token-expiration-date':
            // Mock expiration date - 2 hours from now
            const expirationDate = new Date();
            expirationDate.setHours(expirationDate.getHours() + 2);
            let headers = new HttpHeaders({
                "Content-Type": "text/plain"
            })
            return of(new HttpResponse({
                body: expirationDate.toISOString(),
                headers: headers
            })).pipe(delay(delayMs));

        case '/api/datev/available-apis':
            // Mock API availability data
            return of(new HttpResponse({
                body: [
                    {
                        "api-name": "eau",
                        "accessable": "true",
                        "message": "EAU-Schnittstelle ist verfügbar und funktionsfähig"
                    },
                    {
                        "api-name": "payroll",
                        "accessable": "false",
                        "message": "Lohnabrechnung-Schnittstelle ist derzeit nicht verfügbar"
                    },
                    {
                        "api-name": "accounting",
                        "accessable": "true",
                        "message": "Buchhaltungs-Schnittstelle ist verfügbar"
                    }
                ]
            })).pipe(delay(delayMs));

        case '/api/clients':
            return of(new HttpResponse({
                body: [
                    { id: "client1", name: "Test Client 1" },
                    { id: "client2", name: "Test Client 2" },
                    { id: "client3", name: "Test Client 3" }
                ]
            })).pipe(delay(delayMs));

        case '/api/employees':
            if (environment.production) {
                return next(req);
            } else {
                return of(new HttpResponse({
                    body: [
                        { email: "emp1@test.com", name: "Employee 1", datevEmployeeNumber: "EMP001", employeeId: "EMP001", numberOfNewEaus: 3 },
                        { email: "emp2@test.com", name: "Employee 2", datevEmployeeNumber: "EMP002", employeeId: "EMP002", numberOfNewEaus: 1 },
                        { email: "emp3@test.com", name: "Employee 3", datevEmployeeNumber: "EMP003", employeeId: "EMP003", numberOfNewEaus: 0 }
                    ]
                })).pipe(delay(delayMs));
            }
    }

    // Handle client switch endpoint
    if (req.url.match(/\/api\/datev\/clients\/.*\/switch/)) {
        if (environment.production) {
            return next(req);
        }
        const clientId = req.url.split('/').slice(-2)[0];
        return of(new HttpResponse({
            body: {
                state: clientId === 'client1' ? 200 : 401,
                message: clientId === 'client1' ? 'Successfully connected' : 'Unauthorized'
            }
        })).pipe(delay(delayMs));
    }

    // Handle sick leave endpoint (aktuelles Backend-Modell: GET /eau/all -> Absence[], siehe
    // SickLeaveService.getSickLeaves(). Die frühere {success, data}-Zwischenschicht unter
    // /datev/eau-requests (inkl. einer separaten /datev/eau-requests/employee/:id-Route) existiert
    // serverseitig nicht mehr - die Mitarbeiter-Filterung passiert heute clientseitig über
    // Absence.mitarbeiterID (siehe SickLeaveOverviewComponent.filterSickLeaves()).
    if (req.url.match(/\/api\/eau\/all$/)) {
        if (environment.production) {
            return next(req);
        }
        const mockAbsences: Absence[] = [
            {
                id: 1,
                von: "2024-01-01",
                bis: "2024-01-07",
                abwesenheitenTyp: "Krankheit",
                mitarbeiterID: 1,
                status: "EAU_EINGEREICHT",
                tenantId: 1,
            },
            {
                id: 2,
                von: "2024-01-15",
                bis: "2024-01-20",
                abwesenheitenTyp: "Krankheit",
                mitarbeiterID: 2,
                status: "NEU",
                tenantId: 1,
            },
            {
                id: 3,
                von: "2024-02-01",
                bis: "2024-02-05",
                abwesenheitenTyp: "Krankheit",
                mitarbeiterID: 1,
                status: "FEHLERHAFT",
                tenantId: 1,
                error_grund: "JOB_FEHLER",
                error_herkunft: "eau",
                error_since: "2024-02-05T10:00:00Z",
                error_details: null,
            }
        ];

        return of(new HttpResponse({
            body: mockAbsences
        })).pipe(delay(delayMs));
    }

    // Default fallback
    return next(req);
};